import React from 'react';
import { Button, CircularProgress, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';

interface DialogActionsGlyntProps {
  handleSubmit: () => void;
  closeModal: () => void;
  clearButton?: () => void;
  cancelCypressId: string;
  submitCypressId: string;
  clearCypressId: string;
  clearButtonText?: string;
  showClearButton?: boolean;
  showCancelButton?: boolean;
  showSubmitButton?: boolean;
  submitButtonText?: string;
  submitLoading?: boolean;
  submitDisabled?: boolean;
  clearDisabled?: boolean;
}

const ActionsContainer = styled('div')({
  display: 'flex',
  width: '100%',
});

const ButtonContainerLeft = styled('div')({
  flex: 1,
});

const ButtonContainerRight = styled('div')({
  display: 'flex',
  gap: '10px',
  justifyContent: 'flex-end',
});

const DialogActionsStyled = styled(DialogActions, {
  shouldForwardProp: (prop) => prop !== '$showBorder',
})<{ $showBorder?: boolean }>(({ $showBorder = true }) => ({
  padding: '12px 16px',
  position: 'sticky',
  bottom: 0,
  background: 'white',
  zIndex: 1,
  borderTop: $showBorder ? '1px solid rgba(0,0,0,0.08)' : 'none',
}));

const DialogActionsGlynt: React.FC<DialogActionsGlyntProps> = ({
  handleSubmit,
  closeModal,
  clearButton,
  cancelCypressId,
  submitCypressId,
  clearCypressId,
  clearButtonText = 'Clear',
  showClearButton = false,
  showCancelButton = true,
  showSubmitButton = true,
  submitButtonText = 'Submit',
  submitLoading = false,
  submitDisabled = false,
  clearDisabled = false,
}) => {
  React.useEffect(() => {
    if (!showSubmitButton || submitDisabled || submitLoading) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'TEXTAREA') return;
      if (target?.closest('form')) return;
      // Let MUI pickers, menus, and autocomplete handle Enter themselves.
      if (
        target?.getAttribute('role') === 'option' ||
        target?.closest(
          [
            '.MuiAutocomplete-root',
            '.MuiAutocomplete-popper',
            '.MuiMenu-root',
            '.MuiMenu-paper',
            '.MuiPickersPopper-root',
            '[role="listbox"]',
            '[role="combobox"]',
          ].join(', ')
        )
      ) {
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSubmitButton, submitDisabled, submitLoading, handleSubmit]);

  return (
    <DialogActionsStyled $showBorder={showSubmitButton}>
      <ActionsContainer>
        <ButtonContainerLeft>
          {showCancelButton && showSubmitButton && (
            <Button
              id={cancelCypressId}
              onClick={() => closeModal()}
              data-cy={cancelCypressId}
              disabled={submitLoading}
              sx={{ textTransform: 'none', color: '#5f6d7e', fontSize: '13px' }}
            >
              Cancel
            </Button>
          )}
        </ButtonContainerLeft>
        <ButtonContainerRight>
          {showClearButton && clearButton && (
            <Button
              id={clearCypressId}
              onClick={clearButton}
              data-cy={clearCypressId}
              disabled={clearDisabled}
              sx={{ textTransform: 'none', color: '#5f6d7e', fontSize: '13px' }}
            >
              {clearButtonText}
            </Button>
          )}

          {showSubmitButton ? (
            <Button
              id={submitCypressId}
              onClick={handleSubmit}
              data-cy={submitCypressId}
              disabled={submitLoading || submitDisabled}
              startIcon={submitLoading ? <CircularProgress size={15} /> : null}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                borderRadius: '6px',
                boxShadow: 'none',
                backgroundColor: '#65BBA9',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#579e8f',
                  boxShadow: 'none',
                },
              }}
            >
              {submitButtonText}
            </Button>
          ) : showCancelButton ? (
            <Button
              id={cancelCypressId}
              onClick={() => closeModal()}
              data-cy={cancelCypressId}
              sx={{ textTransform: 'none', color: '#5f6d7e', fontSize: '13px' }}
            >
              Cancel
            </Button>
          ) : null}
        </ButtonContainerRight>
      </ActionsContainer>
    </DialogActionsStyled>
  );
};

export default DialogActionsGlynt;
